import { ReactNode, useState } from "react";
import { Dangerous, InfoOutlined, WarningAmber } from "@mui/icons-material";
import {
  Avatar,
  Box,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Driver,
  Good,
  Scalars,
  Shipment,
  ShipmentEvent,
  ShipmentEventType,
  ShipmentLocation,
  Trip,
  TripAssetTypes,
} from "../../../graphql/generated";
import driverLabel from "../../../utils/labels/driverLabel";
import { formatDateTime } from "../../../utils/labels/formatDateTime";
import ShipmentLink from "../../common/ShipmentLink";
import { shipmentLocationAddressLabel } from "../../../utils/labels/shipmentLocationLabel";
import i18next from "i18next";
import { mapValues } from "lodash";
import AssetLinkContainer from "../../asset-management/AssetLink";
import enumLabel from "../../../utils/labels/enumLabel";
import ImagePreviewDialog from "../../common/ImagePreviewDialog/ImagePreviewDialog";

export enum EventReportType {
  INFO = "info",
  WARNING = "warning",
  DANGER = "danger",
}

export type EventReportData = {
  _id: string;
  type: EventReportType;
  title: string;
  message: ReactNode;
  date: Date;
  shipment: Pick<Shipment, "_id" | "shipmentNumber">;
  trip:
    | (Pick<Trip, "tripNumber" | "_id"> & {
        driver?:
          | Pick<Driver, "firstname" | "lastname" | "_id">
          | undefined
          | null;
      })
    | undefined
    | null;
  metadata?: {
    assetType?: TripAssetTypes;
    assetId?: Scalars["ObjectId"];
  };
};

type ShipmentLocationAddressData = {
  shipper?: {
    address: {
      label: string;
    };
  } | null;
  receiver?: {
    address: {
      label: string;
    };
  } | null;
};

export type EventWithShipmentAndTrip = Omit<
  NonNullable<
    Pick<
      ShipmentEvent,
      | "_id"
      | "type"
      | "date"
      | "shipmentLocation"
      | "label"
      | "description"
      | "metadata"
    >
  >,
  "shipmentLocation"
> & {
  shipment: Pick<Shipment, "_id" | "shipmentNumber">;
  shipmentLocation?: Pick<
    ShipmentLocation,
    "shippedGoods" | "receivedGoods" | "_id"
  > &
    ShipmentLocationAddressData;
  trip:
    | (Pick<Trip, "tripNumber" | "_id"> & {
        driver?:
          | Pick<Driver, "firstname" | "lastname" | "_id">
          | undefined
          | null;
      })
    | undefined
    | null;
  allShippedGoodsById: { [key: string]: Good };
};

export let eventDescriptions: {
  [key in ShipmentEventType]:
    | string
    | ((event: EventWithShipmentAndTrip) => string);
} = {
  [ShipmentEventType.TripStarted]: (event) =>
    `${driverDisplay(event.trip?.driver)} has started trip ${
      event.trip?.tripNumber
    }`,
  [ShipmentEventType.TripEnd]: (event) =>
    `${driverDisplay(event.trip?.driver)} has finished trip ${
      event.trip?.tripNumber
    }`,
  [ShipmentEventType.PickupCheckpointArrive]: (event) =>
    `${driverDisplay(
      event.trip?.driver
    )} has arrived at ${shipmentLocationAddressLabel(event.shipmentLocation)}`,
  [ShipmentEventType.PickupCheckpointLeft]: (event) =>
    `${driverDisplay(
      event.trip?.driver
    )} has left ${shipmentLocationAddressLabel(event.shipmentLocation)}`,
  [ShipmentEventType.PickupCheckpointLoadStart]: (event) =>
    `${driverDisplay(
      event.trip?.driver
    )} is picking up ${event.shipmentLocation?.shippedGoods
      ?.map((shippedGood) => shippedGood.label)
      .join(", ")} at ${shipmentLocationAddressLabel(event.shipmentLocation)}`,
  [ShipmentEventType.DropOffCheckpointArrive]: (event) =>
    `${driverDisplay(
      event.trip?.driver
    )} has arrived at ${shipmentLocationAddressLabel(event.shipmentLocation)}`,
  [ShipmentEventType.DropOffCheckpointLeft]: (event) =>
    `${driverDisplay(
      event.trip?.driver
    )} has left ${shipmentLocationAddressLabel(event.shipmentLocation)}`,
  [ShipmentEventType.DropoffCheckpointUnloadStart]: (event) =>
    `${driverDisplay(
      event.trip?.driver
    )} is dropping ${event.shipmentLocation?.receivedGoods
      ?.map(
        (receivedGood) => event.allShippedGoodsById[receivedGood.goodId]?.label
      )
      .join(", ")} at ${shipmentLocationAddressLabel(event.shipmentLocation)}`,
  [ShipmentEventType.ShipmentComplete]: (event) =>
    `${driverDisplay(event.trip?.driver)} has finished delivering load ${
      event.shipment.shipmentNumber
    }`,
  [ShipmentEventType.TripBreakStarted]: (event) =>
    `${driverDisplay(event.trip?.driver)} went on a break`,
  [ShipmentEventType.TripResumed]: (event) =>
    `${driverDisplay(event.trip?.driver)} resumed the trip`,
  [ShipmentEventType.CheckCall]: (event) =>
    `${event.label || "Check Call"}: ${event.description || ""}`,
  [ShipmentEventType.TripAssetAssignmentUpdated]: (event) => {
    const assetLabel = {
      [TripAssetTypes.Driver]: "driver",
      [TripAssetTypes.Carrier]: "carrier",
      [TripAssetTypes.Tractor]: "tractor",
      [TripAssetTypes.Trailer]: "trailer",
      [TripAssetTypes.AdditionalTrailers]: "additional trailers",
      [TripAssetTypes.Chassis]: "chassis",
    };
    return `The ${
      assetLabel[event.metadata?.assetType as TripAssetTypes]
    } has been updated to `;
  },
  [ShipmentEventType.ShipmentAccepted]: (event) =>
    `Shipment ${event.shipment.shipmentNumber} has been accepted`,
  // Compartment tracking events
  [ShipmentEventType.CompartmentLoaded]: (event: EventWithShipmentAndTrip) => {
    const entries = (event.metadata as any)?.compartmentLoading as
      | Array<{ compartmentId: string; commodityId: string; quantity: number }>
      | undefined;
    if (entries?.length) {
      const lines = entries.map(e => {
        const good = event.allShippedGoodsById[e.commodityId];
        const label = good?.label ?? e.commodityId;
        const unit = good?.unit ?? "";
        const unitSuffix = unit ? ` ${unit}` : "";
        return `${driverDisplay(event.trip?.driver)} loaded ${e.quantity}${unitSuffix} of ${label} into ${e.compartmentId}`;
      });
      return lines.join("; ");
    }
    return `${driverDisplay(event.trip?.driver)} loaded compartments`;
  },
  [ShipmentEventType.CompartmentUnloaded]: (event: EventWithShipmentAndTrip) => {
    const entries = (event.metadata as any)?.compartmentUnloading as
      | Array<{ compartmentId: string; commodityId: string; unloadedQuantity: number; storageFacilityId?: string }>
      | undefined;
    if (entries?.length) {
      const lines = entries.map(e => {
        const good = event.allShippedGoodsById[e.commodityId];
        const label = good?.label ?? e.commodityId;
        const unit = good?.unit ?? "";
        const unitSuffix = unit ? ` ${unit}` : "";
        const tankSuffix = e.storageFacilityId ? ` into ${e.storageFacilityId}` : "";
        return `${driverDisplay(event.trip?.driver)} unloaded ${e.unloadedQuantity}${unitSuffix} of ${label} from ${e.compartmentId}${tankSuffix}`;
      });
      return lines.join("; ");
    }
    return `${driverDisplay(event.trip?.driver)} unloaded compartments`;
  },
};

type EventReportProps = {
  eventReport: EventReportData;
  onClick?: () => void;
  showShipmentLink?: boolean;
};

export const EventReport = ({
  eventReport,
  onClick,
  showShipmentLink = true,
}: EventReportProps) => {
  const theme = useTheme();

  const eventReportTypeColors: { [key in EventReportType]: string } = {
    [EventReportType.INFO]: theme.palette.info.main,
    [EventReportType.WARNING]: theme.palette.warning.light,
    [EventReportType.DANGER]: theme.palette.error.light,
  };

  const eventReportTypeIcons: { [key in EventReportType]: ReactNode } = {
    [EventReportType.INFO]: <InfoOutlined />,
    [EventReportType.WARNING]: <WarningAmber />,
    [EventReportType.DANGER]: <Dangerous />,
  };

  return (
    <ListItem data-testid={`event-report-open-${eventReport._id}`}
      secondaryAction={
        <Typography variant="body2">
          {formatDateTime(eventReport.date)}
        </Typography>
      }
      sx={(theme) => ({
        // Put the secondary action on the same line as the title
        "& .MuiListItemSecondaryAction-root": {
          top: theme.spacing(1),
          transform: "translateY(0)",
          color: theme.palette.primary.main,
        },
        cursor: onClick ? "pointer" : undefined,
      })}
      divider
      onClick={onClick ? () => onClick() : undefined}
    >
      <ListItemAvatar>
        <Avatar
          sx={{
            backgroundColor: eventReportTypeColors[eventReport.type],
          }}
        >
          {eventReportTypeIcons[eventReport.type]}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        sx={{
          pt: 1,
          pb: 1,
          color:
            eventReport.type === EventReportType.DANGER
              ? eventReportTypeColors[eventReport.type]
              : undefined,
        }}
        primary={eventReport.title}
        primaryTypographyProps={{
          fontWeight:
            eventReport.type === EventReportType.DANGER ? 600 : undefined,
        }}
        secondary={
          <>
            {eventReport.message}
            {showShipmentLink && (
              <>
                <br />
                <ShipmentLink shipment={eventReport.shipment} />
              </>
            )}
            {eventReport.metadata?.assetType ? (
              eventReport.metadata.assetId ? (
                eventReport.metadata.assetType ===
                TripAssetTypes.AdditionalTrailers ? (
                  eventReport.metadata.assetId
                    .split(",")
                    .map((assetId, index, array) => (
                      <>
                        <AssetLinkContainer
                          key={assetId}
                          assetType={TripAssetTypes.Trailer}
                          assetId={assetId}
                        />
                        {index < array.length - 1 ? ", " : ""}
                      </>
                    ))
                ) : (
                  <AssetLinkContainer
                    assetType={eventReport.metadata.assetType}
                    assetId={eventReport.metadata.assetId}
                  />
                )
              ) : (
                "N/A"
              )
            ) : null}
          </>
        }
      />
    </ListItem>
  );
};

export let eventTitles: { [key in ShipmentEventType]: string } = {
  [ShipmentEventType.TripStarted]: "Trip started",
  [ShipmentEventType.TripEnd]: "Trip ended",
  [ShipmentEventType.PickupCheckpointArrive]: "Arrived at pickup location",
  [ShipmentEventType.PickupCheckpointLeft]: "Left pickup location",
  [ShipmentEventType.PickupCheckpointLoadStart]:
    "Started loading at pickup location",
  [ShipmentEventType.DropOffCheckpointArrive]: "Arrived at delivery location",
  [ShipmentEventType.DropOffCheckpointLeft]: "Left delivery location",
  [ShipmentEventType.DropoffCheckpointUnloadStart]:
    "Started unloading at delivery location",
  [ShipmentEventType.ShipmentComplete]: "Order completed",
  [ShipmentEventType.TripBreakStarted]: "Break started",
  [ShipmentEventType.TripResumed]: "Trip resumed",
  [ShipmentEventType.CheckCall]: "Check call",
  [ShipmentEventType.TripAssetAssignmentUpdated]:
    "Trip asset assignment updated",
  [ShipmentEventType.ShipmentAccepted]: "Shipment accepted",
  // Compartment tracking events
  [ShipmentEventType.CompartmentLoaded]: "Compartment loaded",
  [ShipmentEventType.CompartmentUnloaded]: "Compartment unloaded",
};

export const driverDisplay = (
  driver:
    | {
        firstname: string;
        lastname: string;
        _id: string;
      }
    | null
    | undefined
) => (driver ? driverLabel(driver) : "The driver");

export type ShipmentIssueMessageProps = {
  content: string;
  attachedDocuments?: string[] | null | undefined;
  trip?:
    | (Pick<Trip, "tripNumber" | "_id"> & {
        driver?:
          | Pick<Driver, "firstname" | "lastname" | "_id">
          | undefined
          | null;
      })
    | undefined
    | null;
};

/** Message body for driver-reported shipment issues (note text + optional photos, preview + download). */
export function ShipmentIssueMessage(issue: ShipmentIssueMessageProps) {
  const docs = (issue.attachedDocuments || []).filter(Boolean);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);

  return (
    <>
      <Typography
        fontWeight="bold"
        variant="body2"
        display="inline"
        component="span"
      >
        {driverDisplay(issue.trip?.driver)}:
      </Typography>
      <Typography variant="body2" display="inline" component="span">
        {" "}
        {issue.content}
      </Typography>
      {docs.length > 0 ? (
        <>
          <Box
            component="span"
            sx={{ display: "block", mt: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            {docs.map((url, index) => (
              <Box data-testid={`event-report-open-attachment-${encodeURIComponent(
                  url
                )}`}
                key={`${url}-${index}`}
                component="img"
                src={url}
                alt=""
                role="button"
                aria-label="Open full-size preview"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setPreviewIndex(index);
                    setPreviewOpen(true);
                  }
                }}
                onClick={() => {
                  setPreviewIndex(index);
                  setPreviewOpen(true);
                }}
                sx={{
                  maxWidth: "100%",
                  maxHeight: 200,
                  display: "block",
                  mt: index > 0 ? 1 : 0,
                  borderRadius: 1,
                  cursor: "pointer",
                  "&:hover": { opacity: 0.92 },
                }}
              />
            ))}
          </Box>
          <ImagePreviewDialog
            open={previewOpen}
            images={docs}
            initialIndex={previewIndex}
            onClose={() => setPreviewOpen(false)}
          />
        </>
      ) : null}
    </>
  );
}

export default EventReport;

i18next.on("initialized", () => {
  eventTitles = mapValues(
    eventTitles,
    (value, type) =>
      // @ts-expect-error
      i18next.t(`trips:events.${type}`) as string
  );

  eventDescriptions = mapValues(
    eventDescriptions,
    (value, type) =>
      typeof value === "function"
        ? value
        : (event: EventWithShipmentAndTrip) =>
            // @ts-expect-error
            i18next.t(`trips:eventDescription.${type}`, {
              driver: driverDisplay(event.trip?.driver),
              shipmentLocation: shipmentLocationAddressLabel(event.shipmentLocation),
              shippedGoods: event.shipmentLocation?.shippedGoods
                ?.map((shippedGood) => shippedGood.label)
                .join(", "),
              receivedGoods: event.shipmentLocation?.receivedGoods
                ?.map(
                  (receivedGood) =>
                    event.allShippedGoodsById[receivedGood.goodId]?.label
                )
                .join(", "),
              tripNumber: event.trip?.tripNumber,
              shipmentNumber: event.shipment.shipmentNumber,
              label: event.label,
              description: event.description,
              assetType: enumLabel(event.metadata?.assetType),
            }) as string
  );
});
